import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { initialRoundName, initialTournamentId } from "../../lib/roundSelection";
import "./generator-workspace-refresh.css";

function stageLabel(stage: string) {
  if (stage === "preparing-context") return "Przygotowanie danych";
  if (stage === "solving-auto") return "Wszystkie solvery liczą równolegle";
  if (stage === "ranking") return "Porównywanie i ranking propozycji";
  if (stage === "queued") return "Oczekuje w kolejce";
  return stage || "Generator pracuje";
}

export function SharedGenerationStatus() {
  const tournamentId = initialTournamentId();
  const round = initialRoundName();
  const [now, setNow] = useState(() => Date.now());
  const preflight = useQuery({
    queryKey: ["shared-generation-status", tournamentId, round],
    queryFn: () => api.generatorPreflight(tournamentId, round),
    enabled: Boolean(tournamentId),
    refetchInterval: 2000,
    staleTime: 0,
  });

  const activeJob = useMemo(() => {
    const jobs = preflight.data?.recentJobs || [];
    return jobs.find((job) => ["queued", "running"].includes(String(job.status || ""))) || null;
  }, [preflight.data?.recentJobs]);

  useEffect(() => {
    if (!activeJob) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [activeJob?.id]);

  if (!activeJob) return null;

  const serverProgress = Math.max(0, Math.min(100, Number(activeJob.progress || 0)));
  const started = Date.parse(String(activeJob.createdAt || ""));
  const elapsedSeconds = Number.isFinite(started) ? Math.max(0, (now - started) / 1000) : 0;
  const estimatedProgress = activeJob.status === "running"
    ? Math.min(92, Math.max(serverProgress, 30 + elapsedSeconds * 0.12))
    : serverProgress;
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  const elapsedRemainder = Math.floor(elapsedSeconds % 60);

  return <aside className="shared-generation-status" aria-live="polite">
    <div className="shared-generation-copy">
      <span className="shared-generation-kicker"><i /> GENEROWANIE TRWA · WSPÓLNY STATUS</span>
      <strong>{stageLabel(String(activeJob.stage || activeJob.status || ""))}</strong>
      <small>To samo zadanie jest widoczne dla wszystkich zalogowanych osób · {elapsedMinutes}:{String(elapsedRemainder).padStart(2, "0")}</small>
    </div>
    <div className="shared-generation-progress">
      <div><span style={{ width: `${estimatedProgress}%` }} /></div>
      <strong>{estimatedProgress.toFixed(1)}%</strong>
      <small>szacowany postęp · serwer {serverProgress}%</small>
    </div>
  </aside>;
}
